<template>
  <b-modal
    v-model="visible"
    title="Edit Rule"
    size="lg"
    @hidden="$emit('close')"
    @ok.prevent="saveRule"
    ok-title="Save"
    ok-variant="success"
    cancel-variant="secondary"
  >
    <div class="row mb-3">
      <div class="col-12">
        <label class="d-block mb-1">Enabled</label>
        <label class="switch switch-sm switch-text switch-info mb-0">
          <input class="switch-input" type="checkbox" v-model="rule.enabled">
          <span data-on="Yes" data-off="No" class="switch-label"></span>
          <span class="switch-handle"></span>
        </label>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-12">
        <label>Alert Name</label>
        <b-form-input
          name="rule-label"
          v-model.trim.lazy="rule.label"
          v-validate="'required|min:3|max:32'"
          :class="{'is-invalid': errors.has('rule-label')}"
        />
        <b-form-text class="is-invalid help" v-show="errors.has('rule-label')">
          {{ errors.first('rule-label') }}
        </b-form-text>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-12">
        <label>Devices</label>
        <multiselect
          v-if="!loading"
          v-model="rule.devices"
          :options="groupedDevices"
          :multiple="true"
          group-values="devices"
          group-label="type"
          :group-select="true"
          placeholder="Select Devices"
          track-by="name"
          label="name"
          :closeOnSelect="true"
        >
          <span slot="noResult">No devices found.</span>
        </multiselect>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-12">
        <label>Send Alerts To</label>
        <multiselect
          v-if="targets && targets.length"
          v-model="rule.targets"
          :options="targets"
          :multiple="true"
          placeholder="Select Alert Targets"
          track-by="id"
          label="label"
          :custom-label="targetLabel"
          :closeOnSelect="true"
        >
          <span slot="noResult">No targets found.</span>
        </multiselect>
        <div v-else class="text-muted small">
          No targets found. <router-link :to="{ name: 'Notification Targets' }">Create one</router-link> or alerts will show on dashboard only.
        </div>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-sm-3">
        <label>Aggregation</label>
        <b-form-select v-model="rule.aggPeriod">
          <option value="0">Live (no aggregation)</option>
          <option value="900">15 Minute Avg.</option>
          <option value="3600">Hourly Avg.</option>
          <option value="86400">Daily Avg.</option>
        </b-form-select>
        <b-form-text v-if="rule.aggPeriod == 0" class="text-warning">
          <i class="fa fa-bolt mr-1"></i> Fires on every incoming reading
        </b-form-text>
      </div>
      <div class="col-sm-3">
        <label>Metric</label>
        <b-form-select
          name="rule-metric"
          v-model="rule.metricName"
          v-validate="'required'"
          :class="{'is-invalid': errors.has('rule-metric')}"
        >
          <option
            v-for="dm in deviceMetrics[deviceType]"
            :value="dm.value"
            :key="dm.value"
          >{{ dm.text }}</option>
        </b-form-select>
        <b-form-text class="is-invalid help" v-show="errors.has('rule-metric')">
          {{ errors.first('rule-metric') }}
        </b-form-text>
      </div>
      <div class="col-sm-3">
        <label>Operator</label>
        <b-form-select v-model="rule.comparator">
          <option value=">">Greater Than</option>
          <option value="<">Less Than</option>
        </b-form-select>
      </div>
      <div class="col-sm-3">
        <label>Value</label>
        <b-form-input
          name="rule-value"
          v-model.trim.lazy="rule.triggerValue"
          v-validate="'required|decimal'"
          :class="{'is-invalid': errors.has('rule-value')}"
        />
        <b-form-text class="is-invalid help" v-show="errors.has('rule-value')">
          {{ errors.first('rule-value') }}
        </b-form-text>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-4">
        <label>Alert Reminder</label>
        <b-form-select v-model="rule.reminderInterval">
          <option value="0">Never</option>
          <option value="3600">Every Hour</option>
          <option value="21600">Every 6 Hours</option>
          <option value="43200">Every 12 Hours</option>
          <option value="86400">Daily</option>
        </b-form-select>
      </div>
      <div class="col-sm-4">
        <label>Alert Expiration</label>
        <b-form-select v-model="rule.expiresIn">
          <option value="0">Never</option>
          <option value="86400">1 Day</option>
          <option value="259200">3 Days</option>
          <option value="604800">1 Week</option>
        </b-form-select>
      </div>
    </div>

    <hr>

    <div class="row">
      <div class="col-12">
        <RuleActions :rule-id="rule.id" :actions="rule.actions || []" @changed="$emit('saved-actions')" />
      </div>
    </div>
  </b-modal>
</template>

<script>
import { Validator } from 'vee-validate'
import Multiselect from 'vue-multiselect'
import RuleActions from './RuleActions.vue'
import { DEVICES_QUERY, UPDATE_RULE_MUTATION, TARGETS_QUERY } from '@/constants/graphql'
import { DEVICE_METRICS } from '@/constants/data'

const dictionary = {
  en: {
    attributes: {
      'rule-value': 'value',
      'rule-label': 'rule name'
    }
  }
}

Validator.localize(dictionary)

export default {
  name: 'EditRule',
  components: { Multiselect, RuleActions },
  props: {
    rule: {
      type: Object,
      default () { return { enabled: true } }
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      visible: this.show,
      loading: 0,
      deviceMetrics: DEVICE_METRICS
    }
  },
  computed: {
    deviceType () {
      return (this.rule && this.rule.devices && this.rule.devices.length)
        ? this.rule.devices[0].type
        : 'well'
    },
    groupedDevices () {
      var groups = {}
      var options = []
      if (this.devices) {
        this.devices.forEach(function (device) {
          var key = device.type.charAt(0).toUpperCase() + device.type.slice(1)
          if (!groups[key]) groups[key] = []
          groups[key].push({ name: device.site.name + ' - ' + device.name, serial: device.serial, type: device.type, id: device.id })
        })
        for (var type in groups) {
          options.push({ type: type, devices: groups[type] })
        }
      }
      return options
    }
  },
  watch: {
    show (val) {
      this.visible = val
    }
  },
  methods: {
    targetLabel (target) {
      return target.label + ' - ' + target.destination
    },
    saveRule () {
      this.$validator.validateAll().then((result) => {
        if (!this.rule.devices || !this.rule.devices.length) {
          this.$swal.fire({ icon: 'warning', title: 'Please select at least one device.' })
          return
        }
        if (!result) return

        var devices = this.rule.devices.map(function (d) { return { id: d.id } })
        var targets = (this.rule.targets || []).map(function (t) { return { id: t.id } })

        var data = Object.assign({}, this.rule)
        data.devices = { connect: devices }
        data.targets = { connect: targets }
        data.type = 'metric'
        data.aggPeriod = parseInt(data.aggPeriod)
        data.aggFunction = data.aggPeriod === 0 ? 'raw' : 'mean'
        data.triggerValue = parseFloat(data.triggerValue)
        data.reminderInterval = parseInt(data.reminderInterval)
        data.expiresIn = parseInt(data.expiresIn)
        delete data.id
        delete data.__typename
        delete data.actions

        this.$apollo.mutate({
          mutation: UPDATE_RULE_MUTATION,
          variables: { data, id: this.rule.id }
        }).then(() => {
          this.$swal.fire({ icon: 'success', title: 'Rule Updated' })
          this.visible = false
          this.$emit('saved')
        }).catch(() => {})
      })
    }
  },
  apollo: {
    targets: { query: TARGETS_QUERY },
    devices: { query: DEVICES_QUERY }
  }
}
</script>
